// All API calls go through same-origin frontend proxy routes — backend URL is never exposed to the browser.
const API_BASE = "/api";

export interface AdminUserUpdatePayload {
  role?: "user" | "admin";
  plan?: "free" | "pro" | "enterprise";
}

export interface AdminToolUpdatePayload {
  is_active?: boolean;
  is_premium?: boolean;
}

export interface EventPayload {
  name: string;
  properties?: Record<string, unknown>;
}

function requiresAuth(endpoint: string): boolean {
  if (endpoint === "/auth/me" || endpoint.startsWith("/auth/me")) return true;
  if (endpoint.startsWith("/tools/") && endpoint.endsWith("/run")) return true;
  if (endpoint.startsWith("/tools/") && endpoint.endsWith("/runs")) return true;
  if (endpoint === "/tools/runs/me" || endpoint.startsWith("/tools/runs/me?")) return true;
  if (endpoint === "/tools/runs/stats" || endpoint.startsWith("/tools/runs/stats?")) return true;
  if (endpoint.startsWith("/budget-planner/")) return true;
  if (endpoint.startsWith("/billing/") && !endpoint.startsWith("/billing/verify-checkout")) return true;
  if (endpoint.startsWith("/admin/")) return true;
  return false;
}

function isReadMethod(method?: string): boolean {
  const verb = (method || "GET").toUpperCase();
  return verb === "GET" || verb === "HEAD";
}

async function fetchWithFailover(
  endpoint: string,
  options: RequestInit,
  headers: Record<string, string>
): Promise<Response> {
  return fetch(API_BASE + endpoint, { ...options, headers });
}

async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem("token");

  if (requiresAuth(endpoint) && !token) {
    throw new Error("Please log in to continue");
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) {
    headers["Authorization"] = "Bearer " + token;
  }
  const res = await fetchWithFailover(endpoint, options, headers);
  const contentType = res.headers.get("content-type") || "";
  const isJsonResponse = contentType.includes("application/json");

  if (!res.ok) {
    if (isJsonResponse) {
      const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
        error?: string;
      };
      throw new Error(err.error || "Request failed");
    }

    const bodyText = await res.text().catch(() => "");
    if (bodyText.trim().startsWith("<")) {
      throw new Error(
        "API endpoint returned HTML instead of JSON. Check that all API routes are properly configured."
      );
    }

    throw new Error(res.statusText || "Request failed");
  }

  if (!isJsonResponse) {
    const bodyText = await res.text().catch(() => "");
    if (bodyText.trim().startsWith("<")) {
      throw new Error(
        "API endpoint returned HTML instead of JSON. Check that all API routes are properly configured."
      );
    }
    throw new Error("Unexpected API response format");
  }

  return res.json();
}

export async function login(email: string, password: string, recaptchaToken?: string) {
  const data = await apiRequest<{ token: string; user: any }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, recaptchaToken }),
  });
  localStorage.setItem("token", data.token);
  return data;
}

export async function register(email: string, password: string, name: string, recaptchaToken?: string) {
  const data = await apiRequest<{ token: string; user: any }>("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name, recaptchaToken }),
  });
  localStorage.setItem("token", data.token);
  return data;
}

export async function logout() {
  try { await apiRequest("/auth/logout", { method: "POST" }); } catch (_) {}
  localStorage.removeItem("token");
}

export async function getMe() {
  return apiRequest<{ user: any }>("/auth/me");
}

export async function updateMe(payload: { name?: string; currentPassword?: string; newPassword?: string }) {
  return apiRequest<{ user: any }>("/auth/me", {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function getTools(category?: string) {
  const qs = category ? "?category=" + encodeURIComponent(category) : "";
  return apiRequest<{ tools: any[] }>("/tools" + qs);
}

export async function getTool(slug: string) {
  return apiRequest<{ tool: any }>("/tools/" + slug);
}

export async function runTool(slug: string, input: Record<string, any>) {
  return apiRequest<any>("/tools/" + slug + "/run", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function getToolRuns(slug: string) {
  return apiRequest<{ runs: any[] }>("/tools/" + slug + "/runs");
}

export async function getFeaturedTools() {
  return apiRequest<{ tools: any[] }>("/marketing/featured-tools");
}

export async function getMarketingStats() {
  return apiRequest<any>("/marketing/stats");
}

export async function submitContact(name: string, email: string, message: string) {
  return apiRequest<any>("/marketing/contact", {
    method: "POST",
    body: JSON.stringify({ name, email, message }),
  });
}

export async function createCheckout(plan: string) {
  return apiRequest<{ url: string }>("/billing/create-checkout", {
    method: "POST",
    body: JSON.stringify({ plan }),
  });
}

export function getGoogleOAuthUrl(): string {
  return "/api/auth/oauth/google/start";
}

export async function getSubscription() {
  return apiRequest<any>("/billing/subscription");
}

export async function createPortalSession() {
  return apiRequest<{ url: string }>("/billing/portal", { method: "POST" });
}

export async function verifyCheckout(sessionId: string) {
  return apiRequest<{ success: boolean; plan: string }>("/billing/verify-checkout", {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId }),
  });
}

export async function getAdminStats() {
  return apiRequest<{ stats: Record<string, unknown> }>("/admin/stats");
}

export async function getAdminUsers(params?: {
  page?: number;
  limit?: number;
  search?: string;
}) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set("page", String(params.page));
  if (params?.limit) qs.set("limit", String(params.limit));
  if (params?.search) qs.set("search", params.search);
  const suffix = qs.toString() ? "?" + qs.toString() : "";
  return apiRequest<any>("/admin/users" + suffix);
}

export async function updateAdminUser(userId: string, payload: AdminUserUpdatePayload) {
  return apiRequest<{ message: string }>("/admin/users/" + userId, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function getAdminTools() {
  return apiRequest<{ tools: any[] }>("/admin/tools");
}

export async function updateAdminTool(toolId: string, payload: AdminToolUpdatePayload) {
  return apiRequest<{ message: string }>("/admin/tools/" + toolId, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function getAdminRuns(limit = 50) {
  return apiRequest<{ runs: any[] }>("/admin/runs?limit=" + encodeURIComponent(String(limit)));
}

export async function trackEvent(payload: EventPayload) {
  return apiRequest<{ ok: boolean }>("/marketing/events", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function getMyRuns(limit = 20) {
  return apiRequest<{ runs: any[] }>("/tools/runs/me?limit=" + encodeURIComponent(String(limit)));
}

export async function getMyStats() {
  return apiRequest<{ runs_today: number; runs_total: number; top_tool: string | null }>("/tools/runs/stats");
}

export async function getBudgetTransactionSyncData() {
  return apiRequest<{ accounts: any[]; transactions: any[] }>("/budget-planner/transaction-sync");
}

export async function saveBudgetConnectedAccounts(accounts: any[]) {
  return apiRequest<{ success: boolean; saved_accounts: number }>("/budget-planner/transaction-sync/accounts", {
    method: "POST",
    body: JSON.stringify({ accounts }),
  });
}

export async function saveBudgetSyncedTransactions(transactions: any[]) {
  return apiRequest<{ success: boolean; saved_transactions: number }>("/budget-planner/transaction-sync/transactions", {
    method: "POST",
    body: JSON.stringify({ transactions }),
  });
}

export async function getBudgetHouseholds() {
  return apiRequest<{ households: any[] }>("/budget-planner/households");
}

export async function createBudgetHousehold(name: string, currency = "USD") {
  return apiRequest<{ success: boolean; household_id: string }>("/budget-planner/households", {
    method: "POST",
    body: JSON.stringify({ name, currency }),
  });
}

export async function getBudgetHouseholdDetail(householdId: string) {
  return apiRequest<{ household: any; members: any[]; categories: any[]; transactions: any[]; notifications: any[] }>(
    "/budget-planner/households/" + encodeURIComponent(householdId)
  );
}

export async function saveBudgetHouseholdMembers(householdId: string, members: any[]) {
  return apiRequest<{ success: boolean; saved_members: number }>(
    "/budget-planner/households/" + encodeURIComponent(householdId) + "/members",
    {
      method: "POST",
      body: JSON.stringify({ members }),
    }
  );
}

export async function saveBudgetHouseholdCategories(householdId: string, categories: any[]) {
  return apiRequest<{ success: boolean; saved_categories: number }>(
    "/budget-planner/households/" + encodeURIComponent(householdId) + "/categories",
    {
      method: "POST",
      body: JSON.stringify({ categories }),
    }
  );
}

export async function saveBudgetHouseholdTransactions(
  householdId: string,
  transactions: any[],
  largePurchaseThreshold: number
) {
  return apiRequest<{ success: boolean; saved_transactions: number }>(
    "/budget-planner/households/" + encodeURIComponent(householdId) + "/transactions",
    {
      method: "POST",
      body: JSON.stringify({ transactions, large_purchase_threshold: largePurchaseThreshold }),
    }
  );
}

export async function markBudgetNotificationRead(householdId: string, notificationId: string) {
  return apiRequest<{ success: boolean }>(
    "/budget-planner/households/" + encodeURIComponent(householdId) + "/notifications/" + encodeURIComponent(notificationId) + "/read",
    {
      method: "POST",
    }
  );
}

// ── Site Settings ────────────────────────────────────────────────────────────

export async function getAdminSettings() {
  return apiRequest<{ settings: Record<string, string>; rows: any[] }>("/admin/settings");
}

export async function updateAdminSettings(settings: Record<string, string>) {
  return apiRequest<{ ok: boolean; updated: number }>("/admin/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}